import React, {useEffect, useMemo, useState} from 'react'
import {
    Box,
    ButtonBase,
    Container,
    Grid,
    Menu,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    ThemeProvider,
    Typography,
    useMediaQuery
} from '@mui/material'
import {fonts, theme} from '../../../config/Dashboardtheme'
import Pagination from '@mui/material/Pagination'
import MpSvgIcon from './MpSvgIcon'
import {useAppDispatch, useAppSelector} from '../../../redux/storeExports'
import ConfirmationModal from './ConfirmationModal'
import ExecutiveUserStatsModal from './ExecutiveUserStatsModal'
import {
    ExecutiveCorporateEmployee,
    ExecutiveEmployeesSelector,
    fetchCode,
    inviteExecutiveUser,
    resendInviteExecutiveUser
} from '../../../redux/corporateService'
import {FetchExecutiveUsers} from '@meprism/shared/src/redux/profile/ProfileSlice'
import {ExecutiveUserProfileDashboard} from './ExecutiveUserProfileDashboard'
import Deleteicon from '@meprism/shared/src/assets/icons/Delete.svg'
import PersonIcon from '@mui/icons-material/Person';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import MailIcon from '@mui/icons-material/Mail';
import RefreshIcon from '@mui/icons-material/Refresh';
import {formatToReadableDate} from '@meprism/shared/src/utils/dateHelpers'

interface propsType {
    searchValue: string
}

function useDebounce(value: string, delay: number) {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
}

const getBillingStatusColor = (status: string | null) => {
    switch (status) {
        case 'paid':
            return '#52c173'
        case 'expired':
        case 'unpaid':
        case null:
            return '#ef6762'
        default:
            return '#6C757D'
    }
}

const getInviteStatusColor = (status: string | null) => {
    switch (status) {
        case 'active':
            return '#52c173'
        case 'accepted':
            return '#27AE60'
        case 'invited':
        case 'queued':
        case 'expired':
        case 'failed':
        case 'deleted':
            return '#ef6762'
        default:
            return '#6C757D'
    }
}

const formatInviteStatus = (status: string | null) => {
    switch (status) {
        case 'queued':
        case 'invited':
            return 'Invited'
        case 'accepted':
            return 'Accepted'
        case 'expired':
            return 'Expired'
        case 'failed':
            return 'Failed'
        case 'deleted':
            return 'Deleted'
        case 'active':
            return 'Active'
        default:
            return status || 'No Invite'
    }
}

const formatBillingStatus = (status: string | null) => {
    switch (status) {
        case 'paid':
            return 'Paid'
        case 'unpaid':
            return 'Unpaid'
        case 'expired':
            return 'Expired'
        case 'waiting':
        case null:
            return 'Waiting'
        default:
            return status || 'N/A'
    }
}

const ExecutiveUserManagementListItem = (prop: propsType) => {

    const dispatch = useAppDispatch();
    const executiveEmployees = useAppSelector(ExecutiveEmployeesSelector)
    const code = useAppSelector((state) => state.corporateService.code);
    const [open, setOpen] = useState(false)
    const [statsOpen, setStatsOpen] = useState(false)
    const [profileDashboardOpen, setProfileDashboardOpen] = useState(false)
    const debouncedSearchValue = useDebounce(prop.searchValue, 500);
    const isInviteFeatureEnabled = !!code?.is_login_enabled;

    // Sorting state
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
    const [sortBy, setSortBy] = useState<string>('col1');

    const [selectedInviteStatus, setSelectedInviteStatus] = useState<string>('');

    // Billing filter state
    const [selectedBillingStatus, setSelectedBillingStatus] = useState<string>('')

    // Get unique billing statuses from employees
    const availableBillingStatuses = useMemo(() => {
        const statuses = Array.from(
            new Set(executiveEmployees.map((emp) => emp.billing_status || 'waiting'))
        )
        return statuses
    }, [executiveEmployees])

    const availableInviteStatuses = useMemo(() => {
        const statuses = Array.from(
            new Set(executiveEmployees.map((emp) => emp.invite_status || 'No Invite'))
        );
        return statuses;
    }, [executiveEmployees]);

    const filteredEmployees = executiveEmployees.filter((employee: ExecutiveCorporateEmployee) => {
        const searchValue = debouncedSearchValue.toLowerCase()
        const matchesSearch =
            employee.name.toLowerCase().includes(searchValue) ||
            employee.email.toLowerCase().includes(searchValue)

        const matchesBilling =
            !selectedBillingStatus ||
            employee.billing_status === selectedBillingStatus ||
            (selectedBillingStatus === 'waiting' && !employee.billing_status)

        const matchesInvite =
            !selectedInviteStatus ||
            employee.invite_status === selectedInviteStatus ||
            (selectedInviteStatus === 'No Invite' && !employee.invite_status);
        return matchesSearch && matchesBilling && matchesInvite;
    });

    const rows = filteredEmployees
        .map((item: ExecutiveCorporateEmployee, index: number) => ({
            id: index + 1,
            col1: item.name,
            col2: item.email,
            col3: item.muid,
            col4: item.invite_status,
            col5: item.billing_status,
            col6: item.stats.brokers_found,
            col7: item.stats.brokers_removed,
            col8: item.stats.brokers_in_progress,
            col9: (item as any).last_scan_date,
        }))

    // Sorting handler
    const handleSort = (columnKey: string) => {
        if (sortBy === columnKey) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(columnKey);
            setSortDirection('asc');
        }
    };

    // Sort rows before rendering
    const sortedRows = [...rows].sort((a, b) => {
        let valueA: any = a[sortBy as keyof typeof a];
        let valueB: any = b[sortBy as keyof typeof b];

        // Handle case-insensitive string sorting (like name)
        if (sortBy === 'col1') {
            valueA = valueA?.toLowerCase?.() ?? '';
            valueB = valueB?.toLowerCase?.() ?? '';
        }

        if (['col5', 'col6', 'col7', 'col8'].includes(sortBy)) {
            valueA = Number(valueA) || 0;
            valueB = Number(valueB) || 0;
        }
        if (valueA < valueB) return sortDirection === 'asc' ? -1 : 1;
        if (valueA > valueB) return sortDirection === 'asc' ? 1 : -1;
        return 0;
    });

    const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
    const [page, setPage] = useState(0)
    const rowsPerPage = 10
    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage - 1)
    }
    useEffect(() => {
        const totalPages = Math.ceil(filteredEmployees.length / rowsPerPage);
        if (page >= totalPages && page !== 0) {
            setPage(totalPages - 1);
        }
    }, [filteredEmployees.length, page, rowsPerPage]);

    {/* Fetch code */
    }
    useEffect(() => {
        dispatch(fetchCode());
    }, [dispatch]);

    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null)
    const isOpen = Boolean(anchorEl)
    const buttonRef = React.useRef<HTMLButtonElement>(null)
    const [openMenuIndex, setOpenMenuIndex] = React.useState<number | null>()
    const [muidrow, setMuidRow] = React.useState<string | null>()
    const handleButtonClick = (
        event: React.MouseEvent<HTMLButtonElement>,
        index: number,
    ) => {
        if (openMenuIndex === index) {
            setOpenMenuIndex(null);
            setAnchorEl(null);
        } else {
            setOpenMenuIndex(index);
            setAnchorEl(event.currentTarget);
            const clickedRow = rows.find((row) => row.id === index);
            setMuidRow(clickedRow?.col3 || null);
        }
    };

    const handleIconClick = (
        index: number,
    ) => {
        if (openMenuIndex === index) {
            setOpenMenuIndex(null);
            setAnchorEl(null);
        } else {
            setOpenMenuIndex(index);
            const clickedRow = rows.find((row) => row.id === index);
            setMuidRow(clickedRow?.col3 || null);
        }
    };

    const close = () => {
        setAnchorEl(null)
        setOpenMenuIndex(null)
        buttonRef.current?.focus()
    }
    const handleopen = () => {
        setOpen(true)
    }
    const handleSendInvite = async (muid: string | null, email: string, status: string) => {
        if (!muid || !code || !email) return;
        if (['invited', 'expired'].includes(status)) {
            await dispatch(resendInviteExecutiveUser({muid, code: code.code}));
        } else {
            await dispatch(inviteExecutiveUser({muid, code: code.code, email}));
        }
        await dispatch(FetchExecutiveUsers())
    };

    // ── FIX: Use percentage-based widths so all columns fit without horizontal scroll ──
    const allColumns = [
        {key: 'col1', name: 'Name', width: '15%'},
        {key: 'col2', name: 'Email', width: '22%'},
        {key: 'col6', name: 'Found', width: '8%'},
        {key: 'col7', name: 'Removed', width: '9%'},
        {key: 'col8', name: 'In Progress', width: '9%'},
        {
            key: 'col4',
            name: 'Invite Status',
            width: '14%',
            headerRenderer: () => (
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                    <Select
                        value={selectedInviteStatus}
                        onChange={(e) => setSelectedInviteStatus(e.target.value)}
                        displayEmpty
                        size="small"
                        sx={{
                            minWidth: 100,
                            maxWidth: '100%',
                            fontSize: '13px',
                            height: '32px',
                            padding: 0,
                        }}
                        renderValue={(selected) => {
                            if (selected === '') {
                                return 'Invite Status';
                            }
                            return formatInviteStatus(selected);
                        }}
                    >
                        <MenuItem value="">All</MenuItem>
                        {availableInviteStatuses.map((status) => (
                            <MenuItem key={status} value={status}>
                                {formatInviteStatus(status)}
                            </MenuItem>
                        ))}
                    </Select>
                </Box>
            ),
            formatter: (props: any) => (
                <span
                    style={{
                        color: getInviteStatusColor(props.row.col4),
                        fontWeight: 500,
                    }}
                >
            {formatInviteStatus(props.row.col4)}
          </span>
            ),
        },
        {
            key: 'col5',
            name: 'Billing Status',
            width: isInviteFeatureEnabled ? '14%' : '16%',
            headerRenderer: () => (
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center',}}>
                    <Select
                        value={selectedBillingStatus}
                        onChange={(e) => setSelectedBillingStatus(e.target.value)}
                        displayEmpty
                        size="small"
                        sx={{
                            minWidth: 100,
                            maxWidth: '100%',
                            fontSize: '13px',
                            height: '32px',
                        }}
                        renderValue={(selected) => {
                            if (selected === '') {
                                return 'Billing Status';
                            }
                            return formatBillingStatus(selected);
                        }}
                    >
                        <MenuItem value="">All</MenuItem>
                        {availableBillingStatuses.map((status) => (
                            <MenuItem key={status} value={status}>
                                {formatBillingStatus(status)}
                            </MenuItem>
                        ))}
                    </Select>
                </Box>
            ),
            formatter: (props: any) => (
                <span
                    style={{
                        color: getBillingStatusColor(props.row.col5),
                        fontWeight: 500,
                    }}
                >
            {formatBillingStatus(props.row.col5)}
          </span>
            ),
        },
        {
            key: 'col3',
            name: 'Actions',
            width: isInviteFeatureEnabled ? '10%' : '8%',
            formatter: (props: any) => (
                <Box key={props.row.col3}>
                    <Container sx={{display: 'flex', width: '100%', justifyContent: 'center', gap: 0.5}}
                               style={{padding: '0'}}>
                        <ButtonBase title='User Profile Management' onClick={() => {
                            handleIconClick(props.row.id)
                            setProfileDashboardOpen(true)
                        }}>
                            <PersonIcon fontSize='small' color='info'/>
                        </ButtonBase>
                        <ButtonBase title='Broker Stats' onClick={() => {
                            handleIconClick(props.row.id)
                            setStatsOpen(true)
                        }}>
                            <LeaderboardIcon fontSize='small'/>
                        </ButtonBase>
                        <ButtonBase title='Remove Employee' onClick={() => {
                            handleIconClick(props.row.id)
                            handleopen()
                        }}>
                            <img src={Deleteicon} style={{width: '15px'}}/>
                        </ButtonBase>

                        {/* Invite Buttons*/}
                        {isInviteFeatureEnabled && (
                            <ButtonBase
                                title={['invited', 'expired'].includes(props.row.col4) ? 'Resend Invite' : 'Send Invite'}
                                onClick={() => {
                                    handleSendInvite(props.row.col3, props.row.col2, props.row.col4);
                                }}
                                disabled={props.row.col4 === 'accepted'}
                            >
                                {props.row.col4 === 'accepted' ? (
                                    <MailIcon sx={{color: '#9e9e9e', fontSize: 22}}/>
                                ) : ['invited', 'expired'].includes(props.row.col4) ? (
                                    <RefreshIcon sx={{color: '#e2b93b', fontSize: 24}}/>
                                ) : (
                                    <MailIcon sx={{color: '#27ae60', fontSize: 22}}/>
                                )}
                            </ButtonBase>
                        )}
                    </Container>
                </Box>
            ),
        },
    ];

    const columns = isInviteFeatureEnabled
        ? allColumns
        : allColumns.filter(col => col.key !== 'col4');

    // ── Shared cell text-overflow style for truncating long content ──
    const truncateStyle: React.CSSProperties = {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    };

    return (
        <ThemeProvider theme={theme}>
            {!isMobile ? (
                <Box
                    style={{height: 'auto', width: '100%', paddingBottom: '16px'}}
                    bgcolor="#fff"
                    borderRadius={5}
                    p={3}>
                    <TableContainer
                        sx={{
                            width: '100%',
                            overflowX: 'hidden',  // FIX: prevent horizontal scroll
                        }}
                    >
                        <Table
                            style={{
                                background: '#fff',
                                color: '#040115',
                                borderRadius: 5,
                                padding: 3,
                                tableLayout: 'fixed',  // FIX: enforce percentage widths
                                width: '100%',
                            }}>
                            <TableHead>
                                <TableRow>
                                    {columns.map((column) => (
                                        <TableCell
                                            key={column.key}
                                            style={{
                                                width: column.width,
                                                borderBottom: '1.5px solid #426BF0',
                                                ...fonts.Inter.SemiBold,
                                                textAlign: ['col3', 'col4', 'col5'].includes(column.key) ? 'center' : 'left',
                                                cursor: ['col1', 'col6', 'col7', 'col8'].includes(column.key) ? 'pointer' : undefined,
                                                userSelect: ['col1', 'col6', 'col7', 'col8'].includes(column.key) ? 'none' : undefined,
                                                paddingLeft: ['col1'].includes(column.key) ? '8px' : '4px',
                                                paddingRight: '4px',
                                                ...truncateStyle,
                                            }}
                                            onClick={
                                                ['col1', 'col6', 'col7', 'col8'].includes(column.key)
                                                    ? () => handleSort(column.key)
                                                    : undefined
                                            }
                                        >
                                            {column.headerRenderer ? (
                                                column.headerRenderer()
                                            ) : (
                                                ['col1', 'col6', 'col7', 'col8'].includes(column.key) ? (
                                                    <TableSortLabel
                                                        active={sortBy === column.key}
                                                        direction={sortBy === column.key ? sortDirection : 'asc'}
                                                        onClick={() => handleSort(column.key)}
                                                        sx={{fontWeight: 600}}
                                                    >
                                                        {column.name}
                                                    </TableSortLabel>
                                                ) : (
                                                    column.name
                                                )
                                            )}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {sortedRows
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map((row) => (
                                        <TableRow key={row.id}>
                                            {columns.map((column) => (
                                                <TableCell
                                                    key={column.key}
                                                    style={{
                                                        borderBottom: '1.5px solid #615F6B',
                                                        width: column.width,
                                                        textAlign: ['col4', 'col5', 'col3'].includes(column.key) ? 'center' : 'left',
                                                        paddingLeft: ['col1'].includes(column.key) ? '8px' : '4px',
                                                        paddingRight: '4px',
                                                        ...truncateStyle,
                                                    }}>
                                                    {column.key === 'col2' ? (
                                                        <Box sx={{overflow: 'hidden'}}>
                                                            <Typography
                                                                sx={{fontSize: 15, ...truncateStyle}}>{row.col2}</Typography>
                                                            <Typography variant="caption" sx={{
                                                                color: '#C67EFF',
                                                                fontSize: 12, ...truncateStyle,
                                                                display: 'block'
                                                            }}>
                                                                {(row.col9 && formatToReadableDate(row.col9)) ? `Last Scan: ${formatToReadableDate(row.col9)}` : "No scan results"}
                                                            </Typography>
                                                        </Box>
                                                    ) : column.key === 'col1' ? (
                                                        <Box sx={{...truncateStyle}} title={row.col1 as string}>
                                                            {row.col1}
                                                        </Box>
                                                    ) : column.formatter ? (
                                                        column.formatter({row})
                                                    ) : (
                                                        row[column.key as keyof typeof row]
                                                    )}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                            </TableBody>
                        </Table>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                mt: 2,
                                padding: 2,
                                borderRadius: 5,
                                background: '#fff',
                            }}>
                            <Pagination
                                variant="outlined"
                                shape="rounded"
                                count={Math.ceil(rows.length / rowsPerPage)}
                                page={page + 1}
                                onChange={handleChangePage}
                            />
                        </Box>
                    </TableContainer>
                    <ConfirmationModal
                        open={open}
                        setOpen={setOpen}
                        muid={muidrow}
                        isExecutive={true}
                    />
                    <ExecutiveUserStatsModal
                        open={statsOpen}
                        setOpen={setStatsOpen}
                        muid={muidrow}
                    />
                    <ExecutiveUserProfileDashboard
                        isModalOpen={profileDashboardOpen}
                        muid={muidrow ? muidrow : ''}
                        setIsModalOpen={setProfileDashboardOpen}
                    />
                </Box>
            ) : (
                <Box
                    style={{height: 'auto', width: '100%'}}
                    bgcolor="#fff"
                    borderRadius={5}
                    p={3}>
                    {rows.map((row) => (
                        <Grid
                            key={row.id}
                            container
                            direction="column"
                            spacing={2}
                            sx={{
                                borderBottom: '1px solid rgba(97, 95, 107, 0.5)',
                                padding: 2,
                                position: 'relative',
                            }}>
                            <Box
                                sx={{
                                    backgroundColor:
                                        openMenuIndex === row.id ? '#ECF0FD' : 'white',
                                    borderRadius: '2px',
                                    position: 'absolute',
                                    top: '0',
                                    mt: 5,
                                    right: '0',
                                }}
                                key={row.id}>
                                <ButtonBase
                                    sx={{borderRadius: '10px'}}
                                    type="button"
                                    onClick={(event) => handleButtonClick(event, row.id)}
                                    ref={buttonRef}
                                    aria-controls={isOpen ? 'simple-menu' : undefined}
                                    aria-expanded={isOpen || undefined}
                                    aria-haspopup="menu">
                                    <MpSvgIcon
                                        icon={
                                            openMenuIndex === row.id ? 'threeDotsBlue' : 'threeDotsNew'
                                        }
                                    />
                                </ButtonBase>
                                <Menu
                                    open={openMenuIndex === row.id}
                                    onClose={close}
                                    anchorEl={anchorEl}
                                    PaperProps={{
                                        style: {
                                            backgroundColor: 'rgba(111, 142, 245, 0.10)',
                                        },
                                    }}
                                    sx={{borderRadius: '20px', opacity: 1}}>
                                    <MenuItem
                                        onClick={() => {
                                            handleopen()
                                            close()
                                        }}>
                                        <Typography variant={'label'} color={'#1A1B20'}>
                                            Remove Employee
                                        </Typography>
                                    </MenuItem>
                                    <MenuItem
                                        onClick={() => {
                                            setProfileDashboardOpen(true)
                                        }}>
                                        <Typography variant={'label'} color={'#1A1B20'}>
                                            Profile Dashboard
                                        </Typography>
                                    </MenuItem>
                                    <MenuItem
                                        onClick={() => {
                                            setStatsOpen(true)
                                        }}>
                                        <Typography variant={'label'} color={'#1A1B20'}>
                                            View Removal Stats
                                        </Typography>
                                    </MenuItem>
                                </Menu>
                            </Box>
                            {columns
                                .filter((col) => col.key !== 'col3')
                                .map((col) => (
                                    <React.Fragment key={col.key}>
                                        <Grid item xs={6}>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'left',
                                                    wordWrap: 'break-word',
                                                    mb: 1,
                                                }}>
                                                <Typography variant="h5">{col.name}</Typography>
                                                {col.key === 'col2' ? (
                                                    <Box>
                                                        <Typography variant="label">{row.col2}</Typography>
                                                        <Typography variant="caption" sx={{
                                                            color: '#C67EFF',
                                                            fontSize: 12,
                                                            display: 'block'
                                                        }}>
                                                            {row.col9 ? `Last Scan: ${formatToReadableDate(row.col9)}` : ''}
                                                        </Typography>
                                                    </Box>
                                                ) : col.key === 'col4' ? (
                                                    <Typography
                                                        variant="label"
                                                        sx={{
                                                            color: getInviteStatusColor(row.col4),
                                                            fontWeight: 500,
                                                        }}
                                                    >
                                                        {formatInviteStatus(row.col4)}
                                                    </Typography>
                                                ) : col.key === 'col5' ? (
                                                    <Typography
                                                        variant="label"
                                                        sx={{
                                                            color: getBillingStatusColor(row.col5),
                                                            fontWeight: 500,
                                                        }}
                                                    >
                                                        {formatBillingStatus(row.col5)}
                                                    </Typography>
                                                ) : (
                                                    <Typography variant="label">
                                                        {row[col.key as keyof typeof row]}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Grid>
                                    </React.Fragment>
                                ))}
                        </Grid>
                    ))}
                    <ConfirmationModal
                        open={open}
                        setOpen={setOpen}
                        muid={muidrow}
                        isExecutive={true}
                    />
                    <ExecutiveUserStatsModal
                        open={statsOpen}
                        setOpen={setStatsOpen}
                        muid={muidrow}
                    />
                    <ExecutiveUserProfileDashboard
                        isModalOpen={profileDashboardOpen}
                        muid={muidrow ? muidrow : ''}
                        setIsModalOpen={setProfileDashboardOpen}
                    />
                </Box>
            )}
        </ThemeProvider>
    )
}

export default ExecutiveUserManagementListItem